import React, { useState, useMemo, useEffect, useRef } from "react";
import { useGeographicUnits } from "../../../hooks/useGeographicUnits";
import IndicatorChart from "../shared/IndicatorChart";
import CityMapLegend from "../shared/CityMapLegend";
import useSelection from '../../../hooks/useSelection';
import "./EconomicIndicatorsPage.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import templateBg from "../../../assets/images/economic_indicators_template.png";
import { useAuth } from '../../../context/AuthContext';

import portadaReporte from "../../../assets/images/portada_reporte_invescasa.png";
import pagina1Invescasa from "../../../assets/images/que_es_invescasa_pagina_1.png";
import pagina2Invescasa from "../../../assets/images/que_es_invescasa_pagina_2.png";

import useIndicatorYearRanges from "../../../hooks/useIndicatorYearRanges";

const indicators = [
  {
    name: "Gross Regional Product percentage change (%) (YoY)",
    key: "grp_pct_change_yoy",
    description: "Average change in Gross Regional Product; tracks regional economic growth.",
    yLabel: "%",
    isPercentage: true,
  },
  {
    name: "Unemployment Rate (%) per year (city)",
    key: "unemployment_rate_pct",
    description: "Average unemployment level; reads labor-market conditions.",
    yLabel: "%",
    isPercentage: true,
  },
  {
    name: "Estimated Resident Population Rate Change (%) per year (city)",
    key: "estimated_resident_population_change_percent",
    description: "Average change in resident population; highlights demographic shifts.",
    yLabel: "%",
    isPercentage: true,
  },
];

export default function EconomicIndicatorsPage() {
  const { places: cities, loading } = useGeographicUnits("City");
  const { membership } = useAuth();
  const [multiMode, setMultiMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const chartRefs = useRef({});
  const indicatorKeys = useMemo(() => indicators.map((i) => i.key), []);
  const { yearRanges, loading: yearsLoading } = useIndicatorYearRanges(indicatorKeys);

  async function getChartImageOf(containerDiv) {
  if (!containerDiv) return null;

  const svg = containerDiv.querySelector("svg.recharts-surface");
  if (!svg) {
    console.warn("No se encontró el SVG dentro del contenedor");
    return null;
  }

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);

  const svgBlob = new Blob([svgString], {
    type: "image/svg+xml;charset=utf-8",
  });

  const url = URL.createObjectURL(svgBlob);

  const imgData = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const rect = svg.getBoundingClientRect();
      const scale = 2;
      const width = rect.width * scale;
      const height = rect.height * scale;

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL("image/png");
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };

    img.onerror = reject;
    img.src = url;
  });

  return imgData;
}

  
  const handleDownloadPDF = async () => {

  try{
  setIsGenerating(true);

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [1920, 1080],
  });

  let pageCounter = 1;

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  /** ============================
   *   TEMPLATE
   * ============================ */
  const templateImg = new Image();
  templateImg.src = templateBg;
  templateImg.crossOrigin = "anonymous";
  await new Promise((resolve) => (templateImg.onload = resolve));

    // ============================
    // LOAD CARDS & IMAGES
    // ============================
  
    const portada1 = new Image();
    portada1.src = portadaReporte;
    portada1.crossOrigin = "anonymous";
  
    const portada2 = new Image();
    portada2.src = pagina1Invescasa;
  
    const portada3 = new Image();
    portada3.src = pagina2Invescasa;
  
    await Promise.all([
      new Promise(r => portada1.onload = r),
      new Promise(r => portada2.onload = r),
      new Promise(r => portada3.onload = r),
    ]);
  
    // ============================
    // PORTADAS
    // ============================
    pdf.addImage(portada1, "PNG", 0, 0, pageWidth, pageHeight);
    pdf.setFont("helvetica", "bold");
  
    pdf.setFontSize(45);       
    pdf.setTextColor("#ffffff");
    pdf.text("Economic Indicators", 380, 685);   
  
    pdf.addPage();
  
    pdf.addImage(portada2, "PNG", 0, 0, pageWidth, pageHeight);
    pdf.addPage();
  
    pdf.addImage(portada3, "PNG", 0, 0, pageWidth, pageHeight);
  
  
    pdf.addPage();
  
    // ============================
    //  TABLE OF CONTENTS 
    // ============================
    //  Background
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);
  
    //  BIG TITLE
    pdf.setFont("helvetica", "bold");
  
    pdf.setFontSize(125); 
    pdf.setTextColor("#FFFFFF");
    pdf.text("Table of Contents", 450, 145);
  
    //  LIST FORMAT
    pdf.setFontSize(38);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor("#0f385d");
  
    //  POSICIONES
    const startX = 450;
    const baseY  = 360;
    const stepY  = 65;
  
    indicators.forEach((indicator, index) => {
      const itemY = baseY + index * stepY;
      const label = `${index + 1}. ${indicator.name}`;
  
      pdf.text(label, startX, itemY);
    });
    
    // === BARRA VERTICAL A LA DERECHA ===
  
    // Posición X de la barra (ajústala si la quieres más afuera)
    const barX = 1550;     
  
    // Grosor de la barra (más grande = barra más gruesa)
    const barWidth = 11;   
  
    // Cálculo de altura dinámica según # de indicadores
    const firstItemY = baseY - 38;     // un poquito por encima del primer texto
    const lastItemY  = baseY + (indicators.length - 1) * stepY + 15;
  
    // Color corporativo
    pdf.setFillColor("#0f385d"); // azul oscuro, cámbialo si quieres
  
    // Dibujar rectángulo
    pdf.rect(barX, firstItemY, barWidth, lastItemY - firstItemY, "F");
    
    pdf.addPage();
  
    await new Promise(r => setTimeout(r, 500));
  /** ============================
   *   EXPORT MAP FIRST PAGE
   * ============================ */
  const mapContainer = document.querySelector(".city-map-legend-container");

  if (mapContainer) {
    const mapCanvas = await html2canvas(mapContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    const mapImg = mapCanvas.toDataURL("image/png");

    // Fondo
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

    // Mapa
    const mapWidth = pageWidth * 0.35;
    const mapHeight = (mapCanvas.height * mapWidth) / mapCanvas.width;

    pdf.addImage(mapImg, "PNG", 650, 190, mapWidth, mapHeight);

    // Encabezado
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(48);
    pdf.setTextColor("#FFFFFF");
    pdf.text("Economic indicators influencing house prices", 435, 90);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(32);
    pdf.text("Map description", 435, 140);

    // Número grande
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(130);
    pdf.setTextColor("#000000");
    pdf.text(`${pageCounter}`, 54, 302);

    pageCounter++;
  }

  /** ============================
   *  EXPORT CHARTS
   * ============================ */
  const chartCards = document.querySelectorAll(".chart-wrapper");
  await new Promise((resolve) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 300);
    });
  });
})

  for (let i = 0; i < chartCards.length; i++) {
    const indicator = indicators[i];
    // Obtiene el contenedor donde está la gráfica
    const wrapperDiv = chartRefs.current[indicator.key];  
    const imgData = await getChartImageOf(wrapperDiv);

    if (!imgData) continue;

    pdf.addPage();
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

        //  Dibujar el título en el PDF
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(32);
    pdf.setTextColor("#000000");
    pdf.text(indicator.name, 200, 310);



      // === LEYENDA DE STATES (varios, en horizontal) ===    
    // Puedes reusar marginX y legendY si quieres:
    const legendStartX = 440;   // posición inicial X
    const legendY = 250;        // posición fija Y
    const spacing = 190;        // espacio entre items
    const dotRadius = 5;        // tamaño del punto (radio)


    const legendStates = Array.isArray(selectedPlaces) && selectedPlaces.length
      ? selectedPlaces
      : [];  // si no hay nada seleccionado, queda vacío

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(23);

    legendStates.slice().reverse().forEach((place, index) => {
      const color = colorMap[place] || "#000000";
      const x = legendStartX + index * spacing;

      // 1. Puntito de color
      pdf.setFillColor(color);
      pdf.circle(x - 10, legendY - 5, dotRadius, "F");

      // 2. Texto del estado
      pdf.setTextColor(color);
      pdf.text(place, x, legendY);
    });

    // volver a un color neutro para el texto que viene después
    pdf.setTextColor("#FFFFFF");

    // 🧷 insertar la gráfica
    const imgWidth =1650;
    const imgHeight = 600;
    const imageY = 350;
    const marginX = 160;

    pdf.addImage(imgData, "PNG", marginX, imageY, imgWidth, imgHeight);


    // Texto AUD Australina Dolar
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(28);
    pdf.setTextColor("#5f5f5f");
    pdf.text("$ AUD", 210,350 );
    // Encabezado
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(48);
    pdf.setTextColor("#FFFFFF");
    pdf.text("Economic indicators influencing house prices", 435, 90);

    // SUBTÍTULO DINÁMICO
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(32);
    pdf.text(
      indicator.description,
      435,
      140
    );

    // Número de página en grande
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(130);
    pdf.setTextColor("#000000");
    pdf.text(`${pageCounter}`, 54, 302);

    pageCounter++;
  }

  pdf.save("economic-indicators-report.pdf");
  }catch (err) {
    console.error("Error generating PDF:", err);
  } finally {
    setIsGenerating(false);
  }
};

  useEffect(() => {
    const status = membership?.status || '';
    const active = String(status).toLowerCase() === 'active';
    setMultiMode(active);
  }, [membership]);

  const { selected, selectedPlaces, handleClick, setSelected } = useSelection({ 
    multi: multiMode, 
    defaultValue: cities && cities.length > 0 ? cities[0] : 'All', 
    places: cities 
  });

  useEffect(() => {
    if (cities && cities.length > 0) {
      setSelected(multiMode ? ['All'] : cities[0]);
    }
  }, [multiMode, cities, setSelected]);

  // ===== Modal state =====
  const [showChartModal, setShowChartModal] = useState(false);
  const [modalIndicator, setModalIndicator] = useState(null);

  const openChartModal = (ind) => {
    setModalIndicator(ind);
    setShowChartModal(true);
    document.body.classList.add("mc-modal-open");
  };
  const closeChartModal = () => {
    setShowChartModal(false);
    setModalIndicator(null);
    document.body.classList.remove("mc-modal-open");
  };
  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeChartModal();
  };
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && closeChartModal();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const sortedCities = useMemo(() => [...(cities || [])].sort(), [cities]);
  const items = loading ? ["All"] : ["All", ...sortedCities];

  const selectedCities = selectedPlaces;

  const colorMap = useMemo(() => {
    const colors = [
      "#DFA135",
      "#E12020",
      "#00A4E4",
      "#34BF49",
      "#04519E",
      "#F47721",
      "#D20962",
      "#7D3F98",
    ];
    const map = {};
    sortedCities.forEach((city, idx) => { map[city] = colors[idx % colors.length]; });
    return map;
  }, [sortedCities]);

  // ===== Sticky/fixed map panel handling =====
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const TOP = 80;
    const EXTRA_TOP = 12;

    const onScrollOrResize = () => {
      if (!wrapperRef.current || !panelRef.current) return;

      const wrapRect = wrapperRef.current.getBoundingClientRect();
      const panelH = panelRef.current.offsetHeight;
      wrapperRef.current.style.minHeight = panelH + "px";

      const shouldFix = wrapRect.top <= TOP;

      if (shouldFix) {
        const width = Math.round(wrapRect.width);
        const left = Math.round(wrapRect.left);

        panelRef.current.style.position = "fixed";
        panelRef.current.style.top = TOP + EXTRA_TOP + "px";
        panelRef.current.style.left = left + "px";
        panelRef.current.style.width = width + "px";
        panelRef.current.classList.add("is-fixed");
      } else {
        panelRef.current.style.position = "";
        panelRef.current.style.top = "";
        panelRef.current.style.left = "";
        panelRef.current.style.width = "";
        panelRef.current.classList.remove("is-fixed");
      }
    };

    onScrollOrResize();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  return (
    <div className="background">
      <div className="panel-modal-container">
        {/* TITLE BAR */}
        <div className="main-title">
          <h2 className="main-title__title">
            Economic indicators influencing house prices
          </h2>
          <button
            className="download-report"
            aria-label="Download report"
            onClick={handleDownloadPDF}
            disabled={!multiMode || isGenerating}
            style={!multiMode || isGenerating ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <span className="icon" aria-hidden>
              {isGenerating ? (
                <span className="spinner"></span>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </span>

            {isGenerating ? "Generating..." : "Download report"}
          </button>
        </div>

        {/* MINI-BANNER */}
        <div className="mini-banner">
          <span className="banner-label">Capital cities</span>
          <div className="tabs">
            {items.map((item) => {
              const isActive = Array.isArray(selected) ? selected.includes(item) : selected === item;
              const isAll = item === "All";
              const isMultiMode = Array.isArray(selected);
              const color = !isAll ? colorMap[item] : "#092463";
              return (
                <button
                  key={item}
                  className={`tab-button${isActive ? " active" : ""}${isAll && !isMultiMode ? " disabled" : ""}`}
                  onClick={() => (isAll && !isMultiMode) ? null : handleClick(item)}
                  aria-pressed={isActive}
                  disabled={isAll && !isMultiMode}
                  style={
                    isActive && !isAll
                      ? { borderColor: color, background: "var(--color-blue-040)" }
                      : undefined
                  }
                >
                  <span
                    className={`tab-radio${isActive ? " active" : ""}`}
                    aria-hidden
                    style={
                      !isAll
                        ? {
                            borderColor: isActive
                              ? color
                              : "var(--color-navy)",
                            color: isActive ? color : undefined,
                          }
                        : undefined
                    }
                  />
                  <span className="tab-label">{item}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* CONTENT: charts + right sidebar */}
        <div className="main-content-economics">
          <div className="indicators-grid">
            {yearsLoading ? (
              <div style={{ padding: 20 }}>Loading years...</div>
            ) : (
              indicators.map((ind) => {
                const range = yearRanges[ind.key] || [2000, new Date().getFullYear()];
                const yearsArray = Array.from(
                  { length: range[1] - range[0] + 1 },
                  (_, i) => range[0] + i
                );

                return (
                  <div
                    className="chart-wrapper"
                    key={ind.key}
                    ref={(el) => {
                      if (el) {
                        chartRefs.current[ind.key] = el;
                      } else {
                        delete chartRefs.current[ind.key];
                      }
                    }}
                  >
                    <div className="chart-title">
                      {ind.name}
                      <button
                        className="info-trigger"
                        type="button"
                        aria-label="Expand chart"
                        onClick={() => openChartModal(ind)}
                      >
                        <svg
                          className="info-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="4 9 4 4 9 4" />
                          <line x1="4" y1="4" x2="10" y2="10" />

                          <polyline points="15 4 20 4 20 9" />
                          <line x1="20" y1="4" x2="14" y2="10" />

                          <polyline points="20 15 20 20 15 20" />
                          <line x1="20" y1="20" x2="14" y2="14" />

                          <polyline points="9 20 4 20 4 15" />
                          <line x1="4" y1="20" x2="10" y2="14" />
                        </svg>
                      </button>
                    </div>

                    <IndicatorChart
                      indicatorName={ind.key}
                      places={sortedCities}
                      selectedPlaces={selectedCities}
                      years={yearsArray}
                      yLabel={ind.yLabel}
                      isPercentage={ind.isPercentage}
                      colors={sortedCities.map((city) => colorMap[city])}
                      showHeader={false}
                    />
                  </div>
                );
              })
            )}
          </div>

          {/* Right column: wrapper keeps layout width; inner card is the fixed panel */}
          <div className="city-map-legend-wrapper" ref={wrapperRef}>
            <div className="city-map-legend-container" ref={panelRef}>
              <CityMapLegend
                selectedCities={sortedCities.filter((city) =>
                  selectedCities.includes(city)
                )}
                colorMap={colorMap}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== MODAL ===== */}
      {showChartModal && modalIndicator && (
        <div
          className="mc-modal__overlay"
          onClick={onOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label="Expanded chart"
        >
          <div className="mc-modal">
            <div className="mc-modal__header">
              <div className="mc-modal__title">
                <div className="mc-modal__title-main">
                  {modalIndicator.name}
                </div>
                <div className="mc-modal__title-sub">
                  {modalIndicator.description}
                </div>
              </div>
              <button
                className="mc-modal__close"
                onClick={closeChartModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mc-modal__body">
              <IndicatorChart
                indicatorName={modalIndicator.key}
                places={sortedCities}
                selectedPlaces={selectedCities}
                years={(() => {
                  const range = yearRanges[modalIndicator.key] || [2000, new Date().getFullYear()];
                  return Array.from(
                    { length: range[1] - range[0] + 1 },
                    (_, i) => range[0] + i
                  );
                })()}
                yLabel={modalIndicator.yLabel}
                isPercentage={modalIndicator.isPercentage}
                colors={sortedCities.map((city) => colorMap[city])}
                showLegend={true}
                showHeader={false}
                height={520}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
